import styles from "./AppDropdownMenu.module.scss";
import { useId, useRef } from "react";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { Icon, IconType } from "../../icons/Icon";
import { classNames } from "primereact/utils";
import { PortalTooltip } from "../tooltip/PortalTooltip";

export interface IDropdownMenuItem {
  label: string;
  icon?: IconType;
  disabled?: boolean;
  tooltip?: string;
  onClick?: () => void;
  separator?: boolean;
  className?: string;
}

interface IAppDropdownMenuProps {
  items: IDropdownMenuItem[];
  triggerIcon?: IconType;
  triggerIconSize?: string | number;
  triggerIconColor?: string;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  disabled?: boolean;
}

export const AppDropdownMenu = ({
  items,
  triggerIcon = IconType.MoreOptions,
  triggerIconSize = "20px",
  triggerIconColor,
  className,
  triggerClassName,
  menuClassName,
  disabled = false,
}: IAppDropdownMenuProps) => {
  const menuRef = useRef<Menu>(null);
  const tooltipId = useId().replace(/:/g, "");

  const menuItems: MenuItem[] = items.map((item, index) => {
    if (item.separator) {
      return { separator: true };
    }

    const hasTooltip = item.disabled && item.tooltip;

    return {
      label: item.label,
      disabled: item.disabled,
      className: classNames(styles.menu_item, item.className, {
        [styles.menu_item_disabled]: item.disabled,
      }),
      template: (menuItem) => {
        const itemContent = (
          <div
            className={classNames(styles.menu_item_content, {
              [styles.menu_item_content_disabled]: item.disabled,
            })}
            onClick={(e) => {
              e.stopPropagation();
              if (!item.disabled) {
                item.onClick?.();
                menuRef.current?.hide(e);
              }
            }}
            role="menuitem"
            tabIndex={item.disabled ? -1 : 0}
          >
            {item.icon && (
              <Icon
                type={item.icon}
                width={18}
                height={18}
                color="#fff"
                className={styles.menu_item_icon}
              />
            )}
            <span className={styles.menu_item_label}>{menuItem.label}</span>
          </div>
        );

        return hasTooltip ? (
          <div
            data-tooltip-id={`dropdown-tooltip-${tooltipId}`}
            data-tooltip-content={item.tooltip}
            className={styles.tooltip_wrapper}
          >
            {itemContent}
          </div>
        ) : (
          itemContent
        );
      },
    };
  });

  const handleTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    menuRef.current?.toggle(e);
  };

  // Check if any items have tooltips
  const hasItemsWithTooltips = items.some(
    (item) => item.disabled && item.tooltip,
  );

  return (
    <div className={classNames(styles.root, className)}>
      <button
        type="button"
        className={classNames(styles.trigger, triggerClassName, {
          [styles.trigger_disabled]: disabled,
        })}
        onClick={handleTriggerClick}
        disabled={disabled}
        aria-haspopup="true"
      >
        <Icon
          type={triggerIcon}
          width={triggerIconSize}
          height={triggerIconSize}
          color={triggerIconColor}
        />
      </button>
      <Menu
        ref={menuRef}
        model={menuItems}
        popup
        appendTo={document.body}
        autoZIndex
        baseZIndex={1000}
        className={classNames(styles.menu, menuClassName)}
      />
      {hasItemsWithTooltips && (
        <PortalTooltip
          id={`dropdown-tooltip-${tooltipId}`}
          place="left"
          className={styles.tooltip}
        >
          <span />
        </PortalTooltip>
      )}
    </div>
  );
};
